import {
  CoreApplicationFeeAnnounce,
  CoreInput,
  CoreOnboardingAnnounce,
  CorePaymentInput,
  CoreSelect,
  MMButton,
  RecurringToggleBox,
  SuccessPane
} from '@components'
import { useHeaderContext } from '@context/HeaderContext'
import ReferenceIcon from '@images/icons/note.svg?react'
import RecipientIcon from '@images/icons/recipient-select.svg?react'
import AccountingCategorySelect from '@withdrawals/components/AccountingCategorySelect'
import styles from '@withdrawals/styles/WithdrawInitiate.module.scss'
import InternalTransferPreview from '@withdrawals/views/InternalTransferPreview'
import {
  MerchantAccountTypes,
  PaymentOptionStatuses,
  responseDoesNotContainsChallenge,
  selectMerchantAccountType,
  useAppSelector,
  useGetActivePaymentSubAccountsQuery,
  useInternalTransferQry,
  useReturnToOrigin,
  useShouldPayApplicationFeeQry
} from 'mmfintech-backend-api'
import { OtpContext, tr } from 'mmfintech-commons'
import { ErrorDisplay } from 'mmfintech-portal-commons'
import { useContext, useEffect, useState } from 'react'
import { useHistory, useLocation } from 'react-router-dom'

type InternalTransferFormProps = {
  useSubAccounts: boolean
}

const InternalTransferForm = ({ useSubAccounts }: InternalTransferFormProps) => {
  const { currentStep, setCurrentStep } = useHeaderContext()
  const { setOtpOnSuccess } = useContext(OtpContext)

  const { subAccounts } = useLocation()?.state || ({} as any)
  const isSubAccountsTransfer = useSubAccounts || subAccounts

  const [result, setResult] = useState(null)

  const history = useHistory()

  const accountType = useAppSelector(selectMerchantAccountType)
  const shouldPayApplicationFee = useShouldPayApplicationFeeQry()

  const { returnToOrigin } = useReturnToOrigin()
  const returnToOriginUrl = returnToOrigin()

  const { data: activePaymentSubAccounts } = useGetActivePaymentSubAccountsQuery(null, {
    skip: !isSubAccountsTransfer
  })

  const {
    formValues,
    accounts,
    accountsError,
    accountsFetching,
    selectedOption,
    selectedAccount,
    setSelectedAccount,
    handlePreview,
    handleInitiate,
    previewError,
    previewLoading,
    resetMutations,
    isRecurringOpen,
    setIsRecurringOpen
  } = useInternalTransferQry({
    validation: 'float',
    onPreviewSuccess: () => setCurrentStep(2),
    onInitiateSuccess: (response: any) => handleInitiateSuccess(response)
  })

  const transferSuccess = (data: any) => {
    setResult(data)
    setCurrentStep(3)
  }

  const handleInitiateSuccess = (data: any): void => {
    if (responseDoesNotContainsChallenge(data)) {
      transferSuccess(data)
    }
  }

  const handleAdvancedSubmit = () => {
    setOtpOnSuccess(() => transferSuccess)
    handleInitiate()
  }

  useEffect(() => {
    return () => {
      setCurrentStep(1)
      setResult(null)
      resetMutations()
    }
  }, [])

  return (
    <>
      {currentStep === 1 && (
        <div className={styles['send-money-container']} data-test='internal-transfer-container'>
          <div className='from-container'>
            <div className='from-to-label-container'>
              <div className='from-to-label'>{tr('FRONTEND.WITHDRAW.INTERNAL_TRANSFER.FROM', 'From')}</div>
            </div>
            <CorePaymentInput
              selectedOption={selectedAccount}
              setSelectedAccount={setSelectedAccount}
              dataTest={'deposit-payment-input-test'}
              options={accounts}
              enableMaxAmountButton
              paymentOption={selectedOption}
              onChange={(value: any) => formValues.setValue('amount', value)}
              value={formValues.getValue('amount')}
              error={formValues.getError('amount')}
              isLoading={accountsFetching}
            />
          </div>

          <div className='to-container'>
            <div className='from-to-label-container'>
              <div className='from-to-label'>{tr('FRONTEND.WITHDRAW.INTERNAL_TRANSFER.TO', 'To')}</div>
            </div>

            {isSubAccountsTransfer ? (
              <CoreSelect
                type='default'
                data-test='destination-account-input'
                label={tr('FRONTEND.WITHDRAW.INTERNAL_TRANSFER.LINKED_ACCOUNTS', 'Linked accounts')}
                options={activePaymentSubAccounts?.map((account: any) => ({
                  label: account?.name || account?.email,
                  value: account.email
                }))}
                onChange={(_: string, value: any) => formValues.setValue('recipientEmail', value)}
                value={formValues.getValue('recipientEmail')}
                LeftIcon={<RecipientIcon />}
              />
            ) : (
              <>
                <CoreInput
                  type='email'
                  name='recipientEmail'
                  data-test='recipient-email'
                  maxLength={50}
                  label={tr('FRONTEND.WITHDRAW.INTERNAL_TRANSFER.EMAIL', 'Email')}
                  {...formValues.registerInput('recipientEmail')}
                  // autoComplete='off'
                />
                <p className='recipient-email-helper' data-test='recipient-email-helper'>
                  {tr(
                    'FRONTEND.WITHDRAW.INTERNAL_TRANSFER.EMAIL_HELPER',
                    "Enter the merchant's email. For company accounts, use the owner's email."
                  )}
                </p>
              </>
            )}
          </div>

          <CoreInput
            type='text'
            name='reference'
            data-test='reference'
            maxLength={50}
            label={tr('FRONTEND.WITHDRAW.INTERNAL_TRANSFER.REFERENCE', 'Reference')}
            {...formValues.registerInput('reference')}
            autoComplete='off'
            LeftIcon={<ReferenceIcon />}
          />

          <AccountingCategorySelect formValues={formValues} />

          <RecurringToggleBox
            onToggle={() => setIsRecurringOpen(!isRecurringOpen)}
            isRecurringOpen={isRecurringOpen}
            recurringDetailsFormValues={formValues}
          />

          {selectedOption?.status === PaymentOptionStatuses.INCOMPLETE_ONBOARDING ||
          (accountType === MerchantAccountTypes.PROSPECT &&
            selectedOption?.status !== PaymentOptionStatuses.AVAILABLE) ? (
            <CoreOnboardingAnnounce />
          ) : null}

          {shouldPayApplicationFee && <CoreApplicationFeeAnnounce />}

          <div className='form-actions'>
            <ErrorDisplay error={[accountsError, previewError]} />

            <MMButton
              type='button'
              size='large'
              variant='alternative'
              caption={tr('FRONTEND.BUTTONS.CONTINUE', 'Continue')}
              onClick={handlePreview}
              loading={previewLoading}
              disabled={accountsFetching || formValues.getFloat('amount') <= 0}
              data-test='button-continue'
              fullWidth
            />
          </div>
        </div>
      )}

      {currentStep === 2 && (
        <InternalTransferPreview
          selectedAccount={selectedAccount}
          formValues={formValues}
          onSubmit={handleAdvancedSubmit}
          onCancel={() => setCurrentStep(1)}
        />
      )}

      {currentStep === 3 && <SuccessPane response={result} onClick={() => history.push(returnToOriginUrl)} />}
    </>
  )
}

export default InternalTransferForm
